import React from "react";
import {storesContext} from "../../../common/stores/stores";
import i18n from "../../../i18n/i18n";
import {IPaginationStore} from "../../../common/stores/paginationStore";
import Report from "../report/report";
import {Utils} from "../../../helpers/utils";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../common/models/httpError";
import {DangerousGoodsRegisterService} from "../../services/dangerousGoodsRegisterService";
import {DateUtils} from "../../../helpers/dateUtils";
import {ValidatedValue} from "../../../helpers/validatedValue";
import {ShipownerModel} from "../../../dictionaries/models/shipowners";
import {FerryDropDownModel} from "../../../dictionaries/models/ferry";
import {ShipownerService} from "../../../dictionaries/services/shipownerService";
import {FerryService} from "../../../dictionaries/services/ferryService";
import DateTimePicker from "../../../common/components/shared/dateTimePicker/dateTimePicker";
import DropDown from "../../../common/components/shared/dropDown/dropDown";
import {DropDownUtils} from "../../../helpers/dropDownUtils";
import {DropDownModel} from "../../../common/models/dropDown";
import Validator from "../../../helpers/validator";
import {DangerousGoodsRegisterModel, DangerousGoodsRegisterFilters} from "../../models/dangerousGoodsRegister";
import {errorMessages} from "../../../helpers/errorMessage";
import TextInput from "../../../common/components/shared/textInput/textInput";
import {UnCodeModel} from "../../../dictionaries/models/unCode";
import {PackingModel} from "../../../dictionaries/models/packing";
import {UnCodeService} from "../../../dictionaries/services/unCodeService";
import {PackingService} from "../../../dictionaries/services/packingService";
import AutocompleteDropdown from "../../../common/components/shared/autocompleteDropdown/autocompleteDropdown";
import {AutocompleteDropDownUtils} from "../../../helpers/autocompleteDropDownUtils";
import {Api} from "../../../helpers/api";
import Loader from "../../../common/components/shared/loader/loader";

interface DangerousGoodsRegisterState {
    isSubmitButtonBlocked: boolean;
    isReportGenerated: boolean;
    generatedReport: DangerousGoodsRegisterModel[] | undefined | null;
    generatedReportResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    selectedShipowner: ValidatedValue<string>;
    selectedFerry: ValidatedValue<string>;
    selectedArrivalFerryFrom: ValidatedValue<Date | null>;
    selectedArrivalFerryTo: ValidatedValue<Date | null>;
    selectedDepartureFerryFrom: ValidatedValue<Date | null>;
    selectedDepartureFerryTo: ValidatedValue<Date | null>;
    selectedVoyageNo: ValidatedValue<string>;
    selectedUnCode: ValidatedValue<string>;
    selectedPacking: ValidatedValue<string>;
    shipowners: ShipownerModel[];
    ferries: FerryDropDownModel[];
    unCodes: UnCodeModel[];
    packing: PackingModel[];
    shipownersResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    ferriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    unCodesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    packingResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

const notEmptyValidator = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

export default class DangerousGoodsRegister extends React.Component <any, DangerousGoodsRegisterState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            isSubmitButtonBlocked: false,
            isReportGenerated: false,
            generatedReportResponse: undefined,
            generatedReport: undefined,
            currentPage: 0,
            selectedShipowner: new ValidatedValue(""),
            selectedFerry: new ValidatedValue(""),
            selectedArrivalFerryFrom: new ValidatedValue(null, [notEmptyValidator]),
            selectedArrivalFerryTo: new ValidatedValue(null, [notEmptyValidator]),
            selectedDepartureFerryFrom: new ValidatedValue(null, [notEmptyValidator]),
            selectedDepartureFerryTo: new ValidatedValue(null, [notEmptyValidator]),
            selectedVoyageNo: new ValidatedValue(""),
            selectedUnCode: new ValidatedValue(""),
            selectedPacking: new ValidatedValue(""),
            shipowners: [],
            ferries: [],
            unCodes: [],
            packing: [],
            shipownersResponse: undefined,
            ferriesResponse: undefined,
            unCodesResponse: undefined,
            packingResponse: undefined
        };
    }

    async componentDidMount() {
        await this.getFerries();
        await this.getPacking();
        await this.getShipowners();
        await this.getUnCodes();
    }

    public render(): JSX.Element {
        const properties = ["ferryName", "shipownerName", "directionType", "voyageNo", "arrivalDepartureTime",
            "actualArrivalDepartureTime", "cargoType", "lorryRegNo", "regNo", "unCodeValue", "unCodeName", "imdgClass", "packingName",
            "packingQuantity", "packingGroup", "weight"];
        const headers = properties.map(key => i18n.t("reports.dangerousGoodsRegister.headers." + key));
        const isDataLoadedOrDataLoadError = (this.state.shipowners.length > 0 && this.state.ferries.length > 0 && this.state.unCodes.length > 0 && this.state.packing.length > 0) ||
            !Api.isSuccess(this.state.shipownersResponse) || !Api.isSuccess(this.state.ferriesResponse) || !Api.isSuccess(this.state.unCodesResponse) || !Api.isSuccess(this.state.packingResponse);

        if (isDataLoadedOrDataLoadError) {
            return (
                <Report
                    className="dangerous-goods-register"
                    filters={this.renderFilters()}
                    onGenerateReport={this.onGenerateReport.bind(this)}
                    generatedReport={this.state.generatedReport}
                    generatedReportResponse={this.state.generatedReportResponse}
                    isReportGenerated={this.state.isReportGenerated}
                    reportTestId="dangerous-goods-register"
                    reportProperties={properties}
                    reportHeaders={headers}
                    reportTableRenderCell={this.renderCell}
                    currentPage={this.state.currentPage}
                    reportService={DangerousGoodsRegisterService}
                    isSummaryReportGenerated={false}
                    selectedFilters={
                        {
                            arrivalFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryFrom.value),
                            arrivalFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryTo.value),
                            departureFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryFrom.value),
                            departureFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryTo.value),
                            ferryEntityId: this.state.selectedFerry.value,
                            shipownerEntityId: this.state.selectedShipowner.value,
                            voyageNo: this.state.selectedVoyageNo.value,
                            unCodeId: this.state.selectedUnCode.value,
                            packingId: this.state.selectedPacking.value
                        } as DangerousGoodsRegisterFilters
                    }
                    isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                    summaryCurrentPage={0}
                    summaryNumberOfElements={0}
                />
            );
        } else {
            return <Loader />;
        }
    }

    private renderFilters(): JSX.Element {
        return (
            <>
                <DateTimePicker
                    label={i18n.t("reports.dangerousGoodsRegister.filters.arrivalFerryFrom")}
                    validatedValue={this.state.selectedArrivalFerryFrom}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedArrivalFerryFrom.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedArrivalFerryFrom, this.state.selectedArrivalFerryTo);
                        this.addNotEmptyValidator(this.state.selectedArrivalFerryFrom, this.state.selectedArrivalFerryTo);
                    }}
                    testId="filters-arrival-ferry-from"
                />
                <DateTimePicker
                    label={i18n.t("reports.dangerousGoodsRegister.filters.arrivalFerryTo")}
                    validatedValue={this.state.selectedArrivalFerryTo}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedArrivalFerryTo.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedArrivalFerryFrom, this.state.selectedArrivalFerryTo);
                        this.addNotEmptyValidator(this.state.selectedArrivalFerryFrom, this.state.selectedArrivalFerryTo);
                    }}
                    testId="filters-arrival-ferry-to"
                />
                <DateTimePicker
                    label={i18n.t("reports.dangerousGoodsRegister.filters.departureFerryFrom")}
                    validatedValue={this.state.selectedDepartureFerryFrom}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDepartureFerryFrom.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDepartureFerryFrom, this.state.selectedDepartureFerryTo);
                        this.addNotEmptyValidator(this.state.selectedDepartureFerryFrom, this.state.selectedDepartureFerryTo);
                    }}
                    testId="filters-departure-ferry-from"
                />
                <DateTimePicker
                    label={i18n.t("reports.dangerousGoodsRegister.filters.departureFerryTo")}
                    validatedValue={this.state.selectedDepartureFerryTo}
                    onChange={(newDate: Date | null) => {
                        this.state.selectedDepartureFerryTo.setValue(newDate);
                        Validator.addValidatorsToDateTimePicker(this.state.selectedDepartureFerryFrom, this.state.selectedDepartureFerryTo);
                        this.addNotEmptyValidator(this.state.selectedDepartureFerryFrom, this.state.selectedDepartureFerryTo);
                    }}
                    testId="filters-departure-ferry-to"
                />
                <DropDown
                    label={i18n.t("reports.dangerousGoodsRegister.filters.shipowner")}
                    items={this.shipownerToDropDownMap}
                    validatedValue={this.state.selectedShipowner}
                    testId="filters-shipowner"
                    withEmptyValue
                    error={!Api.isSuccess(this.state.shipownersResponse) ? i18n.t("errorMessages.shipownersErrorSuffix") : ""}
                    reloadFunc={() => this.getShipowners()}
                    isNoRequired
                />
                <DropDown
                    label={i18n.t("reports.dangerousGoodsRegister.filters.ferry")}
                    items={this.ferryToDropDownMap}
                    validatedValue={this.state.selectedFerry}
                    testId="filters-ferry"
                    withEmptyValue
                    error={!Api.isSuccess(this.state.ferriesResponse) ? i18n.t("errorMessages.ferriesErrorSuffix") : ""}
                    reloadFunc={() => this.getFerries()}
                    isNoRequired
                />
                <TextInput
                    label={`${i18n.t("reports.dangerousGoodsRegister.filters.voyageNo")}`}
                    validatedValue={this.state.selectedVoyageNo}
                    testId="filters-voyage-no"
                    onChange={() => {
                        this.state.selectedVoyageNo.clearValidation();
                    }}
                />
                <AutocompleteDropdown
                    className="element"
                    label={i18n.t("reports.dangerousGoodsRegister.filters.unCode")}
                    options={this.unCodeToDropDownMap()}
                    validatedValue={this.state.selectedUnCode}
                    testId="filters-uncode"
                    error={!Api.isSuccess(this.state.unCodesResponse) ? i18n.t("errorMessages.unCodesErrorSuffix") : ""}
                    reloadFunc={() => this.getUnCodes()}
                    isNoRequired
                />
                <DropDown
                    label={i18n.t("reports.dangerousGoodsRegister.filters.packing")}
                    items={this.packingToDropDownMap}
                    validatedValue={this.state.selectedPacking}
                    testId="filters-packing"
                    withEmptyValue
                    error={!Api.isSuccess(this.state.packingResponse) ? i18n.t("errorMessages.packingErrorSuffix") : ""}
                    reloadFunc={() => this.getPacking()}
                    isNoRequired
                />
            </>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        switch (property) {
            case "directionType":
                return <>{i18n.t(`directionType.${row[property]}`)}</>;
            case "arrivalDepartureTime":
            case "actualArrivalDepartureTime":
                return <>{DateUtils.getFormattedDateWithTime(row[property])}</>;
            case "cargoType":
                return <>{i18n.t(`cargoType.${row[property]}`)}</>;
            default:
                return row[property];
        }
    }

    private addNotEmptyValidator(from: ValidatedValue<Date | null>, to: ValidatedValue<Date | null>) {
        from.addValidators([notEmptyValidator]);
        to.addValidators([notEmptyValidator]);
    }

    private get shipownerToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.shipowners, "entityId");
    }

    private get ferryToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.ferries, "entityId");
    }

    private unCodeToDropDownMap() {
        const unCodes = this.state.unCodes.sort((a,b) => {
            return a.name.localeCompare(b.name);
        });

        if (!unCodes || (unCodes && typeof unCodes.map !== "function") || (unCodes && unCodes.length === 0)) {
            return [];
        }

        const autocomplete = unCodes.map((unCode) => ({
            id: unCode.id,
            label: `${unCode.name} (${unCode.code})`
        }));

        return AutocompleteDropDownUtils.listToAutocompleteDropDownMap(autocomplete, "id");
    }

    private get packingToDropDownMap(): DropDownModel[] {
        return DropDownUtils.listToDropDownMap(this.state.packing);
    }

    private get isValid(): boolean {
        this.state.selectedArrivalFerryFrom.validate();
        this.state.selectedArrivalFerryTo.validate();
        this.state.selectedDepartureFerryFrom.validate();
        this.state.selectedDepartureFerryTo.validate();
        this.state.selectedFerry.validate();
        this.state.selectedShipowner.validate();
        this.state.selectedVoyageNo.validate();
        this.state.selectedUnCode.validate();
        this.state.selectedPacking.validate();

        return this.state.selectedArrivalFerryFrom.isValid && this.state.selectedArrivalFerryTo.isValid
        && this.state.selectedDepartureFerryFrom.isValid && this.state.selectedDepartureFerryTo.isValid
        && this.state.selectedFerry.isValid && this.state.selectedShipowner.isValid
        && this.state.selectedVoyageNo.isValid && this.state.selectedUnCode.isValid && this.state.selectedPacking.isValid;
    }

    private async onGenerateReport() {
        this.setState({generatedReportResponse: undefined});
        const paginationStore = this.context.paginationStore as IPaginationStore;

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});

            const filters = {
                arrivalFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryFrom.value),
                arrivalFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedArrivalFerryTo.value),
                departureFerryFrom: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryFrom.value),
                departureFerryTo: DateUtils.getFormattedDateWithTimeInUTC(this.state.selectedDepartureFerryTo.value),
                ferryEntityId: this.state.selectedFerry.value,
                shipownerEntityId: this.state.selectedShipowner.value,
                voyageNo: this.state.selectedVoyageNo.value.trim(),
                unCodeId: this.state.selectedUnCode.value || "",
                packingId: this.state.selectedPacking.value
            };

            const generatedReportResponse = await DangerousGoodsRegisterService.generateReport(
                filters
            );

            this.setState({
                isSubmitButtonBlocked: false,
                generatedReportResponse,
                generatedReport: generatedReportResponse?.data?.data || null,
                isReportGenerated: true,
                currentPage: generatedReportResponse?.data?.currentPage
            });

            paginationStore.setNumberOfElements(generatedReportResponse?.data?.numberOfElements);
            paginationStore.setNumberOfPages(generatedReportResponse?.data?.numberOfPages);
        }

        Utils.scrollToFirstErrorMessage();

    }

    private async getFerries() {
        const ferriesResponse = await FerryService.getFerriesForDropDown();

        this.setState({
            ferries: (ferriesResponse)?.data || [],
            ferriesResponse,
        });
    }

    private async getShipowners() {
        const shipownersResponse = await ShipownerService.getAll();

        this.setState({
            shipowners: (shipownersResponse)?.data?.data || [],
            shipownersResponse
        });
    }

    private async getUnCodes() {
        const unCodesResponse = await UnCodeService.getAll();

        this.setState({
            unCodes: (unCodesResponse)?.data?.data || [],
            unCodesResponse
        });
    }

    private async getPacking() {
        const packingResponse = await PackingService.getAll();

        this.setState({
            packing: (packingResponse)?.data?.data || [],
            packingResponse,
        });
    }
}